import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import {
  ChevronLeft,
  ChevronRight,
  RotateCcw,
  CheckCircle,
  BookOpen,
  TrendingUp,
  TrendingDown,
  Activity,
  Target,
  AlertTriangle,
  Lightbulb,
  Award,
  Shuffle,
  Shield,
  Brain,
  HelpCircle,
  Check,
  X,
  Calculator,
  Settings,
  FileText,
  Trophy,
  Bookmark,
} from "lucide-react";
import { FLASH_CARDS_DATA } from "@/data/flashCardsData";

const iconMap = {
  activity: Activity,
  trending: TrendingUp,
  alert: AlertTriangle,
  target: Target,
  lightbulb: Lightbulb,
  checklist: FileText,
  buy: TrendingUp,
  sell: TrendingDown,
  calculator: Calculator,
  profit: TrendingUp,
  stop: AlertTriangle,
  shield: Shield,
  scenario: Brain,
  settings: Settings,
  trophy: Trophy,
  psychology: Brain,
  journal: Bookmark,
};

export function FlashCards({ lang = "en" }) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isFlipped, setIsFlipped] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [completedCards, setCompletedCards] = useState(new Set());
  const [shuffledCards, setShuffledCards] = useState(null);
  const [showQuiz, setShowQuiz] = useState(false);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [quizAnswered, setQuizAnswered] = useState(false);
  const [correctAnswers, setCorrectAnswers] = useState(0);
  const [totalQuizzes, setTotalQuizzes] = useState(0);
  
  // Get unique categories
  const CATEGORIES = [...new Set(FLASH_CARDS_DATA.map(c => c.category[lang]))];
  
  // Filter and optionally shuffle cards
  const baseCards = selectedCategory === "all" 
    ? FLASH_CARDS_DATA 
    : FLASH_CARDS_DATA.filter(c => c.category[lang] === selectedCategory);
  
  const filteredCards = shuffledCards || baseCards;
  
  const currentCard = filteredCards[currentIndex];
  const progress = filteredCards.length > 0 
    ? ((completedCards.size / filteredCards.length) * 100) 
    : 0;
  
  // Reset quiz state when card changes
  useEffect(() => {
    setShowQuiz(false);
    setSelectedAnswer(null);
    setQuizAnswered(false);
  }, [currentIndex]);
  
  const handleNext = () => {
    setIsFlipped(false);
    if (currentCard) {
      setCompletedCards(prev => new Set([...prev, currentCard.id]));
    }
    setTimeout(() => {
      setCurrentIndex((prev) => (prev + 1) % filteredCards.length);
    }, 100);
  };
  
  const handlePrev = () => {
    setIsFlipped(false);
    setTimeout(() => {
      setCurrentIndex((prev) => (prev - 1 + filteredCards.length) % filteredCards.length);
    }, 100);
  };
  
  const handleReset = () => {
    setCurrentIndex(0);
    setIsFlipped(false);
    setCompletedCards(new Set());
    setShuffledCards(null);
    setCorrectAnswers(0);
    setTotalQuizzes(0);
  };
  
  const handleShuffle = () => {
    const shuffled = [...baseCards].sort(() => Math.random() - 0.5);
    setShuffledCards(shuffled);
    setCurrentIndex(0);
    setIsFlipped(false);
  };
  
  const handleCategoryChange = (cat) => {
    setSelectedCategory(cat);
    setCurrentIndex(0);
    setIsFlipped(false);
    setShuffledCards(null);
  };
  
  const handleQuizAnswer = (index) => {
    if (quizAnswered) return;
    
    setSelectedAnswer(index);
    setQuizAnswered(true);
    setTotalQuizzes(prev => prev + 1);
    
    if (index === currentCard.quiz.correctIndex) {
      setCorrectAnswers(prev => prev + 1);
    }
  };
  
  const handleShowQuiz = () => {
    setShowQuiz(true);
    setIsFlipped(false);
  };
  
  const getDifficultyColor = (difficulty) => {
    const d = difficulty.toLowerCase();
    if (d.includes("beginner") || d.includes("مبتدئ")) return "bg-emerald-600";
    if (d.includes("intermediate") || d.includes("متوسط")) return "bg-amber-600";
    if (d.includes("advanced") || d.includes("متقدم")) return "bg-red-600";
    return "bg-blue-600";
  };
  
  const getCategoryColor = (category) => {
    if (category.includes("Opening") || category.includes("فتح")) return "border-green-500 text-green-400";
    if (category.includes("Closing") || category.includes("إغلاق")) return "border-red-500 text-red-400";
    if (category.includes("Stop") || category.includes("وقف")) return "border-orange-500 text-orange-400";
    if (category.includes("Technical") || category.includes("الفني")) return "border-blue-500 text-blue-400";
    if (category.includes("Scenario") || category.includes("سيناريو")) return "border-purple-500 text-purple-400";
    if (category.includes("Psychology") || category.includes("سيكولوجية")) return "border-pink-500 text-pink-400";
    return "border-zinc-500 text-zinc-400";
  };
  
  const IconComponent = currentCard ? iconMap[currentCard.icon] || Activity : Activity;
  
  const t = {
    en: {
      flashCards: "Trading Academy",
      learnConcepts: "Master trading decisions step by step",
      progress: "Progress",
      complete: "Complete",
      reset: "Reset",
      shuffle: "Shuffle",
      all: "All Topics",
      tapToReveal: "Tap card to reveal answer",
      prev: "Previous",
      next: "Next",
      congratulations: "Congratulations!",
      completedAllCards: "You've mastered all cards in this category!",
      takeQuiz: "Test Yourself",
      quizScore: "Quiz Score",
      correct: "Correct!",
      incorrect: "Incorrect",
      showAnswer: "Show Answer",
      continueBtn: "Continue",
    },
    ar: {
      flashCards: "أكاديمية التداول",
      learnConcepts: "أتقن قرارات التداول خطوة بخطوة",
      progress: "التقدم",
      complete: "مكتمل",
      reset: "إعادة",
      shuffle: "خلط",
      all: "كل المواضيع",
      tapToReveal: "اضغط على البطاقة لكشف الإجابة",
      prev: "السابق",
      next: "التالي",
      congratulations: "تهانينا!",
      completedAllCards: "لقد أتقنت جميع البطاقات في هذه الفئة!",
      takeQuiz: "اختبر نفسك",
      quizScore: "نتيجة الاختبار",
      correct: "صحيح!",
      incorrect: "خطأ",
      showAnswer: "أظهر الإجابة",
      continueBtn: "استمر",
    }
  }[lang];

  const isRTL = lang === "ar";
  
  return (
    <div className={`space-y-5 pb-16 ${isRTL ? "rtl" : "ltr"}`} dir={isRTL ? "rtl" : "ltr"}>
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="p-3 bg-gradient-to-br from-indigo-500 via-purple-500 to-pink-500 rounded-xl shadow-lg">
            <BookOpen className="w-7 h-7 text-white" />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-white">{t.flashCards}</h2>
            <p className="text-sm text-zinc-400">{t.learnConcepts}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {totalQuizzes > 0 && (
            <Badge className="bg-purple-600 px-3 py-1.5">
              <HelpCircle className="w-3 h-3 mr-1" />
              {t.quizScore}: {correctAnswers}/{totalQuizzes}
            </Badge>
          )}
          <Badge variant="outline" className="text-zinc-300 px-3 py-1.5 text-base">
            {currentIndex + 1} / {filteredCards.length}
          </Badge>
        </div>
      </div>
      
      {/* Progress */}
      <div className="space-y-2">
        <div className="flex justify-between text-sm">
          <span className="text-zinc-400">{t.progress}</span>
          <span className="text-zinc-300 font-medium">{Math.round(progress)}% {t.complete}</span>
        </div>
        <Progress value={progress} className="h-2.5 bg-zinc-800" />
      </div>
      
      {/* Action Buttons */}
      <div className="flex gap-2 flex-wrap">
        <Button variant="outline" size="sm" onClick={handleShuffle} className="gap-1.5">
          <Shuffle className="w-4 h-4" /> {t.shuffle}
        </Button>
        <Button variant="outline" size="sm" onClick={handleReset} className="gap-1.5">
          <RotateCcw className="w-4 h-4" /> {t.reset}
        </Button>
      </div>
      
      {/* Category Filter */}
      <div className="overflow-x-auto pb-2 -mx-2 px-2">
        <div className="flex gap-2 min-w-max">
          <Button
            variant={selectedCategory === "all" ? "default" : "outline"}
            size="sm"
            onClick={() => handleCategoryChange("all")}
            className={`${selectedCategory === "all" ? "bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700" : ""}`}
          >
            {t.all}
          </Button>
          {CATEGORIES.map(cat => (
            <Button
              key={cat}
              variant={selectedCategory === cat ? "default" : "outline"}
              size="sm"
              onClick={() => handleCategoryChange(cat)}
              className={`whitespace-nowrap ${
                selectedCategory === cat 
                  ? "bg-gradient-to-r from-indigo-600 to-purple-600" 
                  : getCategoryColor(cat)
              }`}
            >
              {cat}
            </Button>
          ))}
        </div>
      </div>
      
      {/* Flash Card */}
      {currentCard && !showQuiz && (
        <div 
          className="cursor-pointer select-none"
          onClick={() => setIsFlipped(!isFlipped)}
        >
          <Card className={`min-h-[400px] sm:min-h-[450px] transition-all duration-500 transform ${
            isFlipped 
              ? "bg-gradient-to-br from-zinc-800 via-zinc-850 to-zinc-900" 
              : "bg-gradient-to-br from-zinc-900 via-zinc-850 to-zinc-900"
          } border-2 ${
            isFlipped ? "border-emerald-500/50 shadow-emerald-500/20" : "border-zinc-700 hover:border-indigo-500/50"
          } shadow-xl hover:shadow-2xl`}>
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div className="flex items-center gap-2">
                  <Badge className={`${getDifficultyColor(currentCard.difficulty[lang])} px-3 py-1`}>
                    {currentCard.difficulty[lang]}
                  </Badge>
                  {currentCard.hasQuiz && (
                    <Badge variant="outline" className="border-purple-500 text-purple-400 px-2">
                      <HelpCircle className="w-3 h-3 mr-1" />
                      Quiz
                    </Badge>
                  )}
                </div>
                <Badge variant="outline" className={`${getCategoryColor(currentCard.category[lang])} px-3`}>
                  {currentCard.category[lang]}
                </Badge>
              </div>
            </CardHeader>
            <CardContent className="flex flex-col items-center justify-center min-h-[320px] sm:min-h-[360px] text-center px-4 sm:px-6">
              {!isFlipped ? (
                <div className="space-y-5 w-full">
                  <div className="p-5 bg-gradient-to-br from-indigo-500/20 via-purple-500/20 to-pink-500/20 rounded-2xl inline-block">
                    <IconComponent className="w-14 h-14 sm:w-16 sm:h-16 text-indigo-400" />
                  </div>
                  <h3 className="text-xl sm:text-2xl font-bold text-white leading-tight px-2">
                    {currentCard.question[lang]}
                  </h3>
                  <div className="flex items-center justify-center gap-2 text-zinc-500">
                    <div className="h-px w-8 bg-zinc-700"></div>
                    <p className="text-sm animate-pulse">{t.tapToReveal}</p>
                    <div className="h-px w-8 bg-zinc-700"></div>
                  </div>
                </div>
              ) : (
                <div className="space-y-4 w-full">
                  <div className="flex justify-center">
                    <div className="p-2 bg-emerald-500/20 rounded-full">
                      <CheckCircle className="w-8 h-8 text-emerald-400" />
                    </div>
                  </div>
                  <div className={`text-${isRTL ? "right" : "left"} w-full bg-zinc-800/70 rounded-xl p-5 max-h-[280px] overflow-y-auto`}>
                    <p className="text-sm sm:text-base text-zinc-200 whitespace-pre-line leading-relaxed font-mono">
                      {currentCard.answer[lang]}
                    </p>
                  </div>
                  
                  {/* Quiz Button */}
                  {currentCard.hasQuiz && (
                    <Button 
                      onClick={(e) => { e.stopPropagation(); handleShowQuiz(); }}
                      className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 gap-2"
                    >
                      <HelpCircle className="w-4 h-4" />
                      {t.takeQuiz}
                    </Button>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}
      
      {/* Quiz Mode */}
      {currentCard && showQuiz && currentCard.hasQuiz && (
        <Card className="min-h-[400px] bg-gradient-to-br from-purple-900/30 via-zinc-900 to-pink-900/30 border-2 border-purple-500/50">
          <CardHeader>
            <div className="flex items-center gap-2">
              <HelpCircle className="w-6 h-6 text-purple-400" />
              <span className="text-lg font-bold text-white">{t.takeQuiz}</span>
            </div>
          </CardHeader>
          <CardContent className="space-y-6">
            <h3 className="text-xl font-semibold text-white">
              {currentCard.quiz.question[lang]}
            </h3>
            
            <div className="space-y-3">
              {currentCard.quiz.options.map((option, idx) => (
                <button
                  key={idx}
                  onClick={() => handleQuizAnswer(idx)}
                  disabled={quizAnswered}
                  className={`w-full p-4 rounded-xl text-left transition-all ${
                    quizAnswered
                      ? idx === currentCard.quiz.correctIndex
                        ? "bg-emerald-600/30 border-2 border-emerald-500"
                        : idx === selectedAnswer
                          ? "bg-red-600/30 border-2 border-red-500"
                          : "bg-zinc-800/50 border-2 border-zinc-700"
                      : "bg-zinc-800 border-2 border-zinc-700 hover:border-purple-500 hover:bg-zinc-700/50"
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <span className="w-8 h-8 rounded-full bg-zinc-700 flex items-center justify-center text-sm font-bold">
                      {String.fromCharCode(65 + idx)}
                    </span>
                    <span className="text-white flex-1">{option[lang]}</span>
                    {quizAnswered && idx === currentCard.quiz.correctIndex && (
                      <Check className="w-6 h-6 text-emerald-400" />
                    )}
                    {quizAnswered && idx === selectedAnswer && idx !== currentCard.quiz.correctIndex && (
                      <X className="w-6 h-6 text-red-400" />
                    )}
                  </div>
                </button>
              ))}
            </div>
            
            {/* Quiz Result */}
            {quizAnswered && (
              <div className={`p-4 rounded-xl ${
                selectedAnswer === currentCard.quiz.correctIndex
                  ? "bg-emerald-600/20 border border-emerald-500/50"
                  : "bg-red-600/20 border border-red-500/50"
              }`}>
                <div className="flex items-center gap-2 mb-2">
                  {selectedAnswer === currentCard.quiz.correctIndex ? (
                    <>
                      <CheckCircle className="w-5 h-5 text-emerald-400" />
                      <span className="font-bold text-emerald-400">{t.correct}</span>
                    </>
                  ) : (
                    <>
                      <X className="w-5 h-5 text-red-400" />
                      <span className="font-bold text-red-400">{t.incorrect}</span>
                    </>
                  )}
                </div>
                <p className="text-zinc-300 text-sm">
                  {currentCard.quiz.explanation[lang]}
                </p>
              </div>
            )}
            
            {quizAnswered && (
              <Button onClick={handleNext} className="w-full bg-gradient-to-r from-indigo-600 to-purple-600">
                {t.continueBtn}
                {isRTL ? <ChevronLeft className="w-4 h-4 ml-2" /> : <ChevronRight className="w-4 h-4 ml-2" />}
              </Button>
            )}
          </CardContent>
        </Card>
      )}
      
      {/* Navigation */}
      {!showQuiz && (
        <div className="flex items-center justify-between gap-4 mt-6">
          <Button 
            variant="outline" 
            onClick={handlePrev}
            disabled={filteredCards.length <= 1}
            className="flex-1 sm:flex-none min-w-[100px] h-12 text-base gap-2"
            size="lg"
          >
            {isRTL ? <ChevronRight className="w-5 h-5" /> : <ChevronLeft className="w-5 h-5" />}
            {t.prev}
          </Button>
          
          {/* Progress dots - Desktop only */}
          <div className="hidden sm:flex gap-1.5 flex-wrap justify-center max-w-[300px]">
            {filteredCards.slice(0, 12).map((card, idx) => (
              <button 
                key={card.id}
                onClick={(e) => {
                  e.stopPropagation();
                  setCurrentIndex(idx);
                  setIsFlipped(false);
                }}
                className={`w-3 h-3 rounded-full transition-all ${
                  idx === currentIndex 
                    ? "bg-indigo-500 scale-125" 
                    : completedCards.has(card.id) 
                      ? "bg-emerald-500" 
                      : "bg-zinc-600 hover:bg-zinc-500"
                }`}
              />
            ))}
            {filteredCards.length > 12 && (
              <span className="text-zinc-500 text-xs self-center">+{filteredCards.length - 12}</span>
            )}
          </div>
          
          {/* Mobile: Show card counter */}
          <div className="sm:hidden text-zinc-400 font-medium">
            {currentIndex + 1}/{filteredCards.length}
          </div>
          
          <Button 
            variant="default" 
            onClick={handleNext}
            disabled={filteredCards.length <= 1}
            className="flex-1 sm:flex-none min-w-[100px] h-12 text-base gap-2 bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700"
            size="lg"
          >
            {t.next}
            {isRTL ? <ChevronLeft className="w-5 h-5" /> : <ChevronRight className="w-5 h-5" />}
          </Button>
        </div>
      )}
      
      {/* Achievement */}
      {progress === 100 && (
        <Card className="bg-gradient-to-r from-amber-600/20 via-yellow-600/20 to-amber-600/20 border-amber-500/50">
          <CardContent className="p-6 flex items-center gap-4">
            <div className="p-4 bg-amber-500/20 rounded-full">
              <Award className="w-12 h-12 text-amber-400" />
            </div>
            <div>
              <h4 className="font-bold text-2xl text-amber-400">{t.congratulations}</h4>
              <p className="text-zinc-300">{t.completedAllCards}</p>
              {totalQuizzes > 0 && (
                <p className="text-amber-300 mt-1">
                  {t.quizScore}: {correctAnswers}/{totalQuizzes} ({Math.round((correctAnswers/totalQuizzes)*100)}%)
                </p>
              )}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
